import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, SlidersHorizontal } from 'lucide-react';
import { Button } from './Button';
import { RangeSlider } from './RangeSlider';
interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
}
export interface FilterState {
  priceRange: [number, number];
  depositRange: [number, number];
  distanceRange: [number, number];
  minRating: number;
  conditions: string[];
}
export function FilterModal({
  isOpen,
  onClose,
  onApply
}: FilterModalProps) {
  const [filters, setFilters] = useState<FilterState>({
    priceRange: [0, 100],
    depositRange: [0, 100],
    distanceRange: [0, 10],
    minRating: 0,
    conditions: []
  });
  const handleApply = () => {
    onApply(filters);
    onClose();
  };
  const handleReset = () => {
    setFilters({
      priceRange: [0, 100],
      depositRange: [0, 100],
      distanceRange: [0, 10],
      minRating: 0,
      conditions: []
    });
  };
  const toggleCondition = (condition: string) => {
    setFilters(prev => ({
      ...prev,
      conditions: prev.conditions.includes(condition) ? prev.conditions.filter(c => c !== condition) : [...prev.conditions, condition]
    }));
  };
  return <AnimatePresence data-id="element-254">
      {isOpen && <>
          {/* Backdrop with blur */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" data-id="element-255" />

          {/* Bottom Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[85vh] flex flex-col" data-id="element-256">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-sand/50" data-id="element-257">
              <h2 className="text-xl font-bold text-charcoal" data-id="element-258">Filter by:</h2>
              <button onClick={onClose} className="p-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-259">
                <X size={24} className="text-charcoal" data-id="element-260" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6" data-id="element-261">
              {/* Price Range */}
              <div className="space-y-3" data-id="element-262">
                <div className="flex items-center justify-between" data-id="element-263">
                  <label className="text-sm font-semibold text-charcoal" data-id="element-264">
                    Price
                  </label>
                  <span className="text-sm text-moss-gray" data-id="element-265">
                    ${filters.priceRange[0]} - ${filters.priceRange[1]}
                  </span>
                </div>
                <RangeSlider min={0} max={100} step={5} value={filters.priceRange} onChange={value => setFilters(prev => ({
              ...prev,
              priceRange: value
            }))} formatLabel={v => `$${v}`} data-id="element-266" />
              </div>

              {/* Ratings */}
              <div className="space-y-3" data-id="element-267">
                <label className="text-sm font-semibold text-charcoal" data-id="element-268">
                  Minimum Rating
                </label>
                <div className="flex gap-2" data-id="element-269">
                  {[4, 4.3, 4.9].map(rating => <button key={rating} onClick={() => setFilters(prev => ({
                ...prev,
                minRating: rating
              }))} className={`flex-1 py-2 px-3 rounded-ios text-sm font-medium transition-all ${filters.minRating === rating ? 'bg-dark-green text-white' : 'bg-sand/30 text-charcoal hover:bg-sand/50'}`} data-id="element-270">
                      {rating}+ ★
                    </button>)}
                </div>
              </div>

              {/* Item Condition */}
              <div className="space-y-3" data-id="element-271">
                <label className="text-sm font-semibold text-charcoal" data-id="element-272">
                  Item Condition
                </label>
                <div className="space-y-2" data-id="element-273">
                  {['New', 'Like New', 'Good', 'Fair'].map(condition => <label key={condition} className="flex items-center gap-3 p-3 rounded-ios hover:bg-sand/20 cursor-pointer transition-colors" data-id="element-274">
                      <input type="checkbox" checked={filters.conditions.includes(condition)} onChange={() => toggleCondition(condition)} className="w-5 h-5 rounded border-2 border-sand text-dark-green focus:ring-2 focus:ring-dark-green/20" data-id="element-275" />
                      <span className="text-sm text-charcoal" data-id="element-276">{condition}</span>
                    </label>)}
                </div>
              </div>

              {/* Distance */}
              <div className="space-y-3" data-id="element-277">
                <div className="flex items-center justify-between" data-id="element-278">
                  <label className="text-sm font-semibold text-charcoal" data-id="element-279">
                    Distance from Me
                  </label>
                  <span className="text-sm text-moss-gray" data-id="element-280">
                    {filters.distanceRange[0]} - {filters.distanceRange[1]} mi
                  </span>
                </div>
                <RangeSlider min={0} max={10} step={0.5} value={filters.distanceRange} onChange={value => setFilters(prev => ({
              ...prev,
              distanceRange: value
            }))} formatLabel={v => `${v} mi`} data-id="element-281" />
              </div>

              {/* Deposit */}
              <div className="space-y-3" data-id="element-282">
                <div className="flex items-center justify-between" data-id="element-283">
                  <label className="text-sm font-semibold text-charcoal" data-id="element-284">
                    Deposit (hold)
                  </label>
                  <span className="text-sm text-moss-gray" data-id="element-285">
                    ${filters.depositRange[0]} - ${filters.depositRange[1]}
                  </span>
                </div>
                <RangeSlider min={0} max={100} step={5} value={filters.depositRange} onChange={value => setFilters(prev => ({
              ...prev,
              depositRange: value
            }))} formatLabel={v => `$${v}`} data-id="element-286" />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-sand/50 bg-white space-y-3" data-id="element-287">
              <div className="flex gap-3" data-id="element-288">
                <Button variant="secondary" onClick={handleReset} className="flex-1" data-id="element-289">
                  Reset
                </Button>
                <Button variant="primary" onClick={handleApply} className="flex-1" data-id="element-290">
                  Apply Filters
                </Button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}